import "./styles.css";

interface Project {
  id: string;
  name: string;
  readonly: boolean;
  permissions: string[];
}

interface UserState {
  username: string;
  csrfToken: string;
  directR2?: boolean;
  projects: Project[];
}

interface ObjectAudit {
  owner?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface ListResponse {
  projectId: string;
  prefix: string;
  directories: Array<{ name: string; path: string } & ObjectAudit>;
  files: Array<
    {
      type: "file";
      name: string;
      path: string;
      size: number;
      uploaded: string;
      contentType: string;
    } & ObjectAudit
  >;
}

interface AuditEvent {
  id: string;
  timestamp: string;
  actor: string;
  action: string;
  projectId: string;
  path?: string;
  targetPath?: string;
  directory?: boolean;
  count?: number;
  size?: number;
  targetOwner?: string;
  previousOwner?: string;
  newOwner?: string;
}

interface TransferItem {
  id: string;
  kind: "upload" | "download";
  name: string;
  loaded: number;
  total: number;
  status: "queued" | "uploading" | "resuming" | "downloading" | "retrying" | "done" | "failed";
  speedBps: number;
  lastLoaded: number;
  lastSpeedAt: number;
  note?: string;
}

type FolderFile = File & { webkitRelativePath?: string };
type UploadedPart = { partNumber: number; etag: string };

interface DirectUploadResponse {
  direct: boolean;
  method: "PUT";
  url: string;
  headers: Record<string, string>;
  r2Key?: string;
  expiresIn: number;
}

interface DirectDownloadResponse {
  direct: boolean;
  url: string;
  size?: number;
  etag?: string;
  expiresIn: number;
}

interface XhrUploadResponse {
  text: string;
  headers: Record<string, string>;
}

interface StoredMultipartUpload {
  id: string;
  projectId: string;
  path: string;
  fileName: string;
  size: number;
  lastModified: number;
  contentType: string;
  partSize: number;
  uploadId: string;
  parts: UploadedPart[];
  updatedAt: string;
}

interface ContextMenuItem {
  path: string;
  name: string;
  directory: boolean;
  size?: number;
  owner?: string;
  createdBy?: string;
  x: number;
  y: number;
}

interface FileSystemWritable {
  write(data: Blob | BufferSource | string | { type: "write"; position: number; data: Blob | BufferSource | string }): Promise<void>;
  close(): Promise<void>;
  abort?(): Promise<void>;
}

interface FileSystemHandle {
  createWritable(): Promise<FileSystemWritable>;
}

const APP_NAME = "Shinano Workspace";
const DEFAULT_PART_SIZE = 32 * 1024 * 1024;
const UPLOAD_CONCURRENCY = 4;
const DOWNLOAD_CHUNK_SIZE = 16 * 1024 * 1024;
const DOWNLOAD_CONCURRENCY = 6;
const MEMORY_DOWNLOAD_LIMIT = 512 * 1024 * 1024;
const appNode = document.querySelector("#app") as HTMLDivElement | null;
if (appNode === null) throw new Error("Missing app root");
const app = appNode;

const state: {
  user: UserState | null;
  projectId: string;
  prefix: string;
  listing: ListResponse | null;
  loading: boolean;
  error: string;
  uploads: TransferItem[];
  dragging: boolean;
  auditOpen: boolean;
  auditLoading: boolean;
  auditEvents: AuditEvent[];
  contextMenu: ContextMenuItem | null;
} = {
  user: null,
  projectId: "",
  prefix: "",
  listing: null,
  loading: true,
  error: "",
  uploads: [],
  dragging: false,
  auditOpen: false,
  auditLoading: false,
  auditEvents: [],
  contextMenu: null
};

const ACTIVE_TRANSFER_STATUSES = new Set<TransferItem["status"]>([
  "queued",
  "uploading",
  "resuming",
  "downloading",
  "retrying"
]);

installBeforeUnloadGuard();
installContextMenuDismiss();
void boot();

async function boot() {
  try {
    const user = await api<UserState>("/api/me");
    state.user = user;
    state.projectId = user.projects[0]?.id || "";
    await loadFiles();
  } catch {
    state.user = null;
  } finally {
    state.loading = false;
    render();
  }
}

async function loadFiles() {
  if (!state.projectId) return;
  state.loading = true;
  state.error = "";
  render();
  try {
    const params = new URLSearchParams({
      projectId: state.projectId,
      prefix: state.prefix
    });
    state.listing = await api<ListResponse>(`/api/files?${params}`);
  } catch (error) {
    state.error = errorMessage(error);
  } finally {
    state.loading = false;
    render();
  }
}

async function loadAudit() {
  if (!state.projectId || !hasPermission("audit")) return;
  state.auditLoading = true;
  render();
  try {
    const params = new URLSearchParams({ projectId: state.projectId, limit: "100" });
    const response = await api<{ events: AuditEvent[] }>(`/api/audit?${params}`);
    state.auditEvents = response.events;
  } catch (error) {
    state.error = errorMessage(error);
  } finally {
    state.auditLoading = false;
    render();
  }
}

function render() {
  if (!state.user) {
    renderLogin();
    return;
  }
  const project = currentProject();
  app.innerHTML = `
    <div class="shell">
      <header class="topbar">
        <div class="brand">
          <div class="brand-mark">S</div>
          <div>
            <h1 class="brand-title">${APP_NAME}</h1>
            <p class="brand-subtitle">${escapeHtml(state.user.username)}${project ? ` · ${escapeHtml(project.name)}` : ""}</p>
          </div>
        </div>
        <div class="top-actions">
          <select class="project-select" id="projectSelect" aria-label="项目"></select>
          <button class="btn ghost" id="logoutBtn" type="button">退出</button>
        </div>
      </header>
      <main class="main">
        <div class="toolbar">
          <div class="breadcrumbs" id="breadcrumbs"></div>
          <div class="button-row">
            ${hasPermission("audit") ? `<button class="btn" id="auditBtn" type="button">操作记录</button>` : ""}
            ${hasPermission("upload") ? `<button class="btn" id="folderUploadBtn" type="button">上传文件夹</button>` : ""}
            ${hasPermission("upload") ? `<button class="btn" id="fileUploadBtn" type="button">上传文件</button>` : ""}
            ${hasPermission("createFolder") ? `<button class="btn primary" id="newFolderBtn" type="button">新建文件夹</button>` : ""}
          </div>
        </div>
        ${project?.readonly ? `<div class="status">当前项目为只读。</div>` : ""}
        ${state.error ? `<div class="error">${escapeHtml(state.error)}</div>` : ""}
        <section class="drop-zone ${state.dragging ? "dragging" : ""}" id="dropZone">
          ${renderTable()}
        </section>
        ${renderAuditPanel()}
        ${renderUploads()}
      </main>
      ${renderContextMenu()}
    </div>
  `;

  bindAppEvents();
}

function renderLogin() {
  app.innerHTML = `
    <main class="login-wrap">
      <section class="login-panel">
        <h1>${APP_NAME}</h1>
        <p>项目文件协作空间</p>
        <form class="form-stack" id="loginForm" method="post" action="/api/login" autocomplete="on">
          <label class="field" for="username">
            <span>用户</span>
            <input id="username" name="username" type="text" autocomplete="username" autocapitalize="none" spellcheck="false" required />
          </label>
          <label class="field" for="password">
            <span>密码</span>
            <input id="password" name="password" type="password" autocomplete="current-password" required />
          </label>
          <button class="btn primary" type="submit">登录</button>
          <div class="error" id="loginError" hidden></div>
        </form>
      </section>
    </main>
  `;

  const form = document.querySelector("#loginForm") as HTMLFormElement | null;
  const errorBox = document.querySelector("#loginError") as HTMLDivElement | null;
  form?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const formData = new FormData(form);
    try {
      const user = await api<UserState>("/api/login", {
        method: "POST",
        body: {
          username: String(formData.get("username") || ""),
          password: String(formData.get("password") || "")
        }
      });
      state.user = user;
      state.projectId = user.projects[0]?.id || "";
      state.prefix = "";
      state.auditOpen = false;
      await loadFiles();
    } catch (error) {
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = errorMessage(error);
      }
    }
  });
}

function renderTable() {
  if (state.loading) return `<div class="empty-state">加载中</div>`;
  const listing = state.listing;
  if (!listing || (!listing.directories.length && !listing.files.length)) {
    return `<div class="empty-state">当前目录为空</div>`;
  }

  const directoryRows = (listing?.directories || [])
    .map(
      (item) => `
        <tr data-context-path="${escapeAttr(item.path)}" data-context-kind="directory" data-context-name="${escapeAttr(item.name)}" data-context-owner="${escapeAttr(item.owner || "")}" data-context-created-by="${escapeAttr(item.createdBy || "")}">
          <td>
            <div class="file-name">
              ${renderFileIcon(item.name, "directory")}
              <button type="button" data-open-dir="${escapeAttr(item.path)}">${escapeHtml(item.name)}</button>
            </div>
          </td>
          <td class="muted">文件夹</td>
          <td class="muted">${escapeHtml(item.owner || item.createdBy || "未记录")}</td>
          <td class="muted">${renderActor(item.createdBy, item.createdAt)}</td>
          <td class="muted">${renderActor(item.updatedBy || item.createdBy, item.updatedAt || item.createdAt)}</td>
          <td>${renderActions(item, true)}</td>
        </tr>
      `
    )
    .join("");

  const fileRows = (listing?.files || [])
    .map(
      (item) => `
        <tr data-context-path="${escapeAttr(item.path)}" data-context-kind="file" data-context-name="${escapeAttr(item.name)}" data-context-size="${escapeAttr(String(item.size || 0))}" data-context-owner="${escapeAttr(item.owner || "")}" data-context-created-by="${escapeAttr(item.createdBy || "")}">
          <td>
            <div class="file-name">
              ${renderFileIcon(item.name, "file", item.contentType)}
              <button type="button" data-download="${escapeAttr(item.path)}" ${hasPermission("download") ? "" : "disabled"}>${escapeHtml(item.name)}</button>
            </div>
          </td>
          <td>${formatBytes(item.size)}</td>
          <td class="muted">${escapeHtml(item.owner || item.createdBy || "未记录")}</td>
          <td class="muted">${renderActor(item.createdBy, item.createdAt)}</td>
          <td class="muted">${renderActor(item.updatedBy || item.createdBy, item.updatedAt || item.uploaded)}</td>
          <td>${renderActions(item, false)}</td>
        </tr>
      `
    )
    .join("");

  return `
    <table class="table">
      <thead>
        <tr>
	          <th>名称</th>
	          <th>大小</th>
	          <th>所属</th>
	          <th>创建</th>
	          <th>最近操作</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>${directoryRows}${fileRows}</tbody>
    </table>
  `;
}

function renderFileIcon(name: string, kind: "directory" | "file", contentType = "") {
  const type = kind === "directory" ? "folder" : fileIconType(name, contentType);
  const icon = {
    folder:
      '<path d="M3.5 6.5a2 2 0 0 1 2-2h4.1l1.8 2h7.1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z"/><path d="M3.5 9h17"/>',
    archive:
      '<path d="M6 3.5h8l4 4v13H6z"/><path d="M14 3.5v4h4"/><path d="M10 5.5h2"/><path d="M11 7.5h2"/><path d="M10 9.5h2"/><path d="M11 11.5h2"/><path d="M10 13.5h2"/><path d="M10 16.5h3"/>',
    image:
      '<path d="M6 3.5h8l4 4v13H6z"/><path d="M14 3.5v4h4"/><circle cx="10" cy="11" r="1.3"/><path d="m8 17 3-3 2 2 1.5-1.5L17 17"/>',
    code:
      '<path d="M6 3.5h8l4 4v13H6z"/><path d="M14 3.5v4h4"/><path d="m11 12-2 2 2 2"/><path d="m14 12 2 2-2 2"/>',
    unity:
      '<path d="M12 4.5 18 8v7l-6 3.5L6 15V8z"/><path d="M12 4.5V11l6 4"/><path d="m6 8 6 3 6-3"/><path d="M12 11v7.5"/>',
    document:
      '<path d="M6 3.5h8l4 4v13H6z"/><path d="M14 3.5v4h4"/><path d="M9 12h6"/><path d="M9 15h6"/><path d="M9 18h4"/>'
  }[type];
  const label = type === "folder" ? "文件夹" : "文件";
  return `<span class="file-icon file-icon--${type}" aria-label="${label}" title="${label}">
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${icon}</svg>
  </span>`;
}

function fileIconType(name: string, contentType: string) {
  const extension = basename(name).toLowerCase().split(".").pop() || "";
  if (["zip", "rar", "7z", "tar", "gz", "bz2", "xz"].includes(extension)) return "archive";
  if (["png", "jpg", "jpeg", "gif", "webp", "psd", "tga", "bmp", "exr"].includes(extension)) return "image";
  if (["cs", "js", "ts", "json", "shader", "cginc", "hlsl", "xml", "yaml", "yml"].includes(extension)) return "code";
  if (["unity", "unitypackage", "prefab", "mat", "asset", "controller", "anim", "fbx", "blend"].includes(extension)) return "unity";
  if (contentType.startsWith("image/")) return "image";
  if (contentType.includes("json") || contentType.startsWith("text/")) return "code";
  return "document";
}

function renderActions(
  item: { path: string; name?: string; size?: number; owner?: string; createdBy?: string },
  directory: boolean
) {
  const buttons = [];
  const name = item.name || basename(item.path);
  if (!directory && hasPermission("download")) {
    buttons.push(
      renderActionButton({
        icon: "download",
        label: "下载",
        attrs: `data-download="${escapeAttr(item.path)}" data-name="${escapeAttr(name)}"`
      })
    );
  }
  if (canDeleteItem(item)) {
    const attr = directory ? "data-delete-dir" : "data-delete-file";
    buttons.push(
      renderActionButton({
        icon: "trash",
        label: "删除",
        danger: true,
        attrs: `${attr}="${escapeAttr(item.path)}"`
      })
    );
  }
  return buttons.length ? `<div class="action-row">${buttons.join("")}</div>` : `<span class="muted">无</span>`;
}

function renderActionButton(options: {
  icon: "download" | "parallelDownload" | "rename" | "move" | "copy" | "owner" | "trash";
  label: string;
  attrs: string;
  title?: string;
  danger?: boolean;
}) {
  const title = options.title || options.label;
  return `<button class="action-btn${options.danger ? " danger" : ""}" type="button" ${options.attrs} aria-label="${escapeAttr(options.label)}" title="${escapeAttr(title)}">
    ${renderActionIcon(options.icon)}
    <span class="sr-only">${escapeHtml(options.label)}</span>
  </button>`;
}

function renderActionIcon(icon: "download" | "parallelDownload" | "rename" | "move" | "copy" | "owner" | "trash") {
  const paths = {
    download: '<path d="M12 4v10"/><path d="m7.5 10.5 4.5 4.5 4.5-4.5"/><path d="M5 19h14"/>',
    parallelDownload:
      '<path d="M8 4v11"/><path d="m4.5 11.5 3.5 3.5 3.5-3.5"/><path d="M16 4v11"/><path d="m12.5 11.5 3.5 3.5 3.5-3.5"/><path d="M4.5 20h15"/>',
    rename:
      '<path d="M4 20h4.5L19 9.5a2.1 2.1 0 0 0 0-3L17.5 5a2.1 2.1 0 0 0-3 0L4 15.5z"/><path d="m13.5 6 4.5 4.5"/>',
    move:
      '<path d="M4 12h13"/><path d="m13 7 5 5-5 5"/><path d="M4 5h7"/><path d="M4 19h7"/>',
    copy:
      '<rect x="8" y="8" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1"/>',
    owner:
      '<path d="M12 3.5 18.5 6v5.2c0 4-2.5 7.2-6.5 8.8-4-1.6-6.5-4.8-6.5-8.8V6z"/><circle cx="12" cy="10.5" r="2"/><path d="M8.5 16.2a4.1 4.1 0 0 1 7 0"/>',
    trash:
      '<path d="M5 6h14"/><path d="M9 6V4h6v2"/><path d="M7 6l.7 14h8.6L17 6"/><path d="M10.5 10v6"/><path d="M13.5 10v6"/>'
  }[icon];
  return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${paths}</svg>`;
}

function renderContextMenu() {
  const item = state.contextMenu;
  if (!item) return "";
  const actions: string[] = [];
  if (!item.directory && hasPermission("download")) {
    actions.push(renderContextMenuItem("download", "浏览器下载", "download"));
    actions.push(renderContextMenuItem("fastDownload", "多线程下载", "parallelDownload"));
  }
  if (canRenameItem(item)) actions.push(renderContextMenuItem("rename", "重命名", "rename"));
  if (canMoveItem(item)) actions.push(renderContextMenuItem("move", "移动到", "move"));
  if (canCopyItem()) actions.push(renderContextMenuItem("copy", "复制到", "copy"));
  if (canTakeOwnership(item)) actions.push(renderContextMenuItem("takeOwnership", "接管所属权", "owner"));
  if (canDeleteItem(item)) actions.push(renderContextMenuItem("delete", "删除", "trash", true));
  if (!actions.length) return "";

  const left = Math.max(8, Math.min(item.x, window.innerWidth - 220));
  const top = Math.max(8, Math.min(item.y, window.innerHeight - 320));
  return `<div class="context-menu" id="contextMenu" role="menu" style="left:${left}px;top:${top}px">
    <div class="context-menu-title">${escapeHtml(item.name)}</div>
    ${actions.join("")}
  </div>`;
}

function renderContextMenuItem(
  action: string,
  label: string,
  icon: "download" | "parallelDownload" | "rename" | "move" | "copy" | "owner" | "trash",
  danger = false
) {
  return `<button class="context-menu-item${danger ? " danger" : ""}" type="button" data-menu-action="${escapeAttr(action)}" role="menuitem">
    ${renderActionIcon(icon)}
    <span>${escapeHtml(label)}</span>
  </button>`;
}

function renderAuditPanel() {
  if (!state.auditOpen || !hasPermission("audit")) return "";
  const body = state.auditLoading
    ? `<div class="empty-state compact">加载中</div>`
    : state.auditEvents.length
      ? `
        <table class="table">
          <thead>
            <tr>
              <th>时间</th>
              <th>用户</th>
              <th>动作</th>
              <th>路径</th>
              <th>数量</th>
            </tr>
          </thead>
          <tbody>
            ${state.auditEvents
              .map(
                (event) => `
                  <tr>
                    <td class="muted">${formatDate(event.timestamp)}</td>
                    <td>${escapeHtml(event.actor)}</td>
                    <td>${escapeHtml(actionLabel(event.action))}</td>
                    <td class="muted">${escapeHtml(auditPathLabel(event))}</td>
                    <td class="muted">${event.count ?? (event.size ? formatBytes(event.size) : "-")}</td>
                  </tr>
                `
              )
              .join("")}
          </tbody>
        </table>
      `
      : `<div class="empty-state compact">暂无操作记录</div>`;
  return `
    <section class="audit-panel">
      <div class="panel-head">
        <h2>操作记录</h2>
        <button class="btn" id="refreshAuditBtn" type="button">刷新</button>
      </div>
      ${body}
    </section>
  `;
}

function renderUploads() {
  if (!state.uploads.length) return "";
  return `
    <div class="upload-panel">
      ${state.uploads
        .slice(-6)
        .map((item) => {
          const percent = item.total
            ? Math.min(100, Math.round((item.loaded / item.total) * 100))
            : item.status === "done"
              ? 100
              : 0;
          return `
            <div class="upload-item">
              <span>${escapeHtml(item.name)} · ${transferStatusLabel(item)} · ${percent}% · ${formatBytes(item.loaded)} / ${formatBytes(item.total)} · ${formatSpeed(item.speedBps)}${item.note ? ` · ${escapeHtml(item.note)}` : ""}</span>
              <div class="progress"><span style="width:${percent}%"></span></div>
            </div>
          `;
        })
        .join("")}
    </div>
  `;
}

function bindAppEvents() {
  const projectSelect = document.querySelector("#projectSelect") as HTMLSelectElement | null;
  if (projectSelect && state.user) {
    projectSelect.innerHTML = state.user.projects
      .map(
        (project) =>
          `<option value="${escapeAttr(project.id)}" ${project.id === state.projectId ? "selected" : ""}>${escapeHtml(project.name)}</option>`
      )
      .join("");
    projectSelect.addEventListener("change", async () => {
      state.projectId = projectSelect.value;
      state.prefix = "";
      state.auditOpen = false;
      state.auditEvents = [];
      await loadFiles();
    });
  }

  document.querySelector("#logoutBtn")?.addEventListener("click", async () => {
    if (!confirmDiscardActiveTransfers()) return;
    await api("/api/logout", { method: "POST", body: {} });
    state.user = null;
    state.listing = null;
    render();
  });

  document.querySelector("#auditBtn")?.addEventListener("click", async () => {
    state.auditOpen = !state.auditOpen;
    if (state.auditOpen && !state.auditEvents.length) await loadAudit();
    else render();
  });
  document.querySelector("#refreshAuditBtn")?.addEventListener("click", () => {
    void loadAudit();
  });

  renderBreadcrumbs();

  document.querySelector("#fileUploadBtn")?.addEventListener("click", () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.onchange = () => {
      if (input.files) void uploadFiles(Array.from(input.files));
    };
    input.click();
  });

  document.querySelector("#folderUploadBtn")?.addEventListener("click", () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.setAttribute("webkitdirectory", "");
    input.onchange = () => {
      if (input.files) void uploadFiles(Array.from(input.files) as FolderFile[]);
    };
    input.click();
  });

  document.querySelector("#newFolderBtn")?.addEventListener("click", async () => {
    const name = window.prompt("文件夹名称");
    if (!name) return;
    try {
      await api("/api/folder", {
        method: "POST",
        body: {
          projectId: state.projectId,
          path: joinPath(state.prefix, name)
        }
      });
      await loadFiles();
    } catch (error) {
      state.error = errorMessage(error);
      render();
    }
  });

  document.querySelectorAll("[data-open-dir]").forEach((button) => {
    const element = button as HTMLButtonElement;
    element.addEventListener("click", async () => {
      state.prefix = element.dataset.openDir || "";
      await loadFiles();
    });
  });

  document.querySelectorAll("[data-context-path]").forEach((row) => {
    const element = row as HTMLElement;
    element.addEventListener("contextmenu", (event) => {
      event.preventDefault();
      state.contextMenu = {
        path: element.dataset.contextPath || "",
        name: element.dataset.contextName || basename(element.dataset.contextPath || ""),
        directory: element.dataset.contextKind === "directory",
        size: Number(element.dataset.contextSize || "0"),
        owner: element.dataset.contextOwner || undefined,
        createdBy: element.dataset.contextCreatedBy || undefined,
        x: event.clientX,
        y: event.clientY
      };
      render();
    });
  });

  document.querySelectorAll("[data-menu-action]").forEach((button) => {
    const element = button as HTMLButtonElement;
    element.addEventListener("click", async () => {
      const action = element.dataset.menuAction || "";
      await handleContextAction(action);
    });
  });

  document.querySelectorAll("[data-download]").forEach((button) => {
    const element = button as HTMLButtonElement;
    element.addEventListener("click", () => {
      const path = element.dataset.download || "";
      triggerBrowserDownload(path, element.dataset.name || basename(path));
    });
  });

  document.querySelectorAll("[data-fast-download]").forEach((button) => {
    const element = button as HTMLButtonElement;
    element.addEventListener("click", async () => {
      const path = element.dataset.fastDownload || "";
      const name = element.dataset.name || basename(path);
      const size = Number(element.dataset.size || "0");
      await downloadFast(path, name, size);
    });
  });

  document.querySelectorAll("[data-take-file]").forEach((button) => {
    const element = button as HTMLButtonElement;
    element.addEventListener("click", async () => {
      const path = element.dataset.takeFile || "";
      if (!window.confirm(`接管 ${basename(path)}？原所属用户将不能再删除它。`)) return;
      await takeOwnership(path, false);
    });
  });

  document.querySelectorAll("[data-take-dir]").forEach((button) => {
    const element = button as HTMLButtonElement;
    element.addEventListener("click", async () => {
      const path = element.dataset.takeDir || "";
      if (!window.confirm(`接管 ${basename(path)} 及其全部文件？原所属用户将不能再删除它们。`)) return;
      await takeOwnership(path, true);
    });
  });

  document.querySelectorAll("[data-rename-file]").forEach((button) => {
    const element = button as HTMLButtonElement;
    element.addEventListener("click", async () => {
      const path = element.dataset.renameFile || "";
      await renamePath(path, false, element.dataset.name || basename(path));
    });
  });

  document.querySelectorAll("[data-rename-dir]").forEach((button) => {
    const element = button as HTMLButtonElement;
    element.addEventListener("click", async () => {
      const path = element.dataset.renameDir || "";
      await renamePath(path, true, element.dataset.name || basename(path));
    });
  });

  document.querySelectorAll("[data-delete-file]").forEach((button) => {
    const element = button as HTMLButtonElement;
    element.addEventListener("click", async () => {
      const path = element.dataset.deleteFile || "";
      if (!window.confirm(`删除 ${basename(path)}？`)) return;
      await deletePath(path, false);
    });
  });

  document.querySelectorAll("[data-delete-dir]").forEach((button) => {
    const element = button as HTMLButtonElement;
    element.addEventListener("click", async () => {
      const path = element.dataset.deleteDir || "";
      if (!window.confirm(`删除 ${basename(path)} 及其全部文件？`)) return;
      await deletePath(path, true);
    });
  });

  const dropZone = document.querySelector("#dropZone") as HTMLElement | null;
  dropZone?.addEventListener("dragenter", (event) => {
    event.preventDefault();
    if (!hasPermission("upload")) return;
    state.dragging = true;
    render();
  });
  dropZone?.addEventListener("dragover", (event) => {
    event.preventDefault();
  });
  dropZone?.addEventListener("dragleave", () => {
    state.dragging = false;
    render();
  });
  dropZone?.addEventListener("drop", (event) => {
    event.preventDefault();
    state.dragging = false;
    if (!hasPermission("upload")) {
      render();
      return;
    }
    const files = event.dataTransfer?.files;
    if (files?.length) void uploadFiles(Array.from(files));
  });
}

function renderBreadcrumbs() {
  const breadcrumbs = document.querySelector("#breadcrumbs") as HTMLDivElement | null;
  if (!breadcrumbs) return;
  const parts = state.prefix.split("/").filter(Boolean);
  const buttons = [`<button class="breadcrumb" type="button" data-prefix="">根目录</button>`];
  let built = "";
  for (const part of parts) {
    built = joinPath(built, part);
    buttons.push(
      `<span class="muted">/</span><button class="breadcrumb" type="button" data-prefix="${escapeAttr(built)}">${escapeHtml(part)}</button>`
    );
  }
  breadcrumbs.innerHTML = buttons.join("");
  breadcrumbs.querySelectorAll("[data-prefix]").forEach((button) => {
    const element = button as HTMLButtonElement;
    element.addEventListener("click", async () => {
      state.prefix = element.dataset.prefix || "";
      await loadFiles();
    });
  });
}

async function deletePath(path: string, directory: boolean) {
  try {
    await api("/api/files", {
      method: "DELETE",
      body: { projectId: state.projectId, path, directory }
    });
    await loadFiles();
  } catch (error) {
    state.error = errorMessage(error);
    render();
  }
}

async function takeOwnership(path: string, directory: boolean) {
  try {
    await api("/api/ownership", {
      method: "POST",
      body: { projectId: state.projectId, path, directory }
    });
    await loadFiles();
    if (state.auditOpen) await loadAudit();
  } catch (error) {
    state.error = errorMessage(error);
    render();
  }
}

async function renamePath(path: string, directory: boolean, currentName: string) {
  const name = window.prompt("新名称", currentName);
  if (!name || name === currentName) return;
  try {
    await api("/api/rename", {
      method: "POST",
      body: { projectId: state.projectId, path, directory, name }
    });
    await loadFiles();
    if (state.auditOpen) await loadAudit();
  } catch (error) {
    state.error = errorMessage(error);
    render();
  }
}

async function movePath(path: string, directory: boolean) {
  const currentParent = parentFolder(path);
  const targetDirectory = window.prompt("移动到文件夹路径（留空表示根目录）", currentParent);
  if (targetDirectory === null) return;
  try {
    await api("/api/move", {
      method: "POST",
      body: { projectId: state.projectId, path, directory, targetDirectory }
    });
    state.prefix = normalizeFolderForState(state.prefix);
    await loadFiles();
    if (state.auditOpen) await loadAudit();
  } catch (error) {
    state.error = errorMessage(error);
    render();
  }
}

async function copyPath(path: string, directory: boolean, currentName: string) {
  const currentParent = parentFolder(path);
  const targetDirectory = window.prompt("复制到文件夹路径（留空表示根目录）", currentParent);
  if (targetDirectory === null) return;
  const normalizedTarget = normalizeFolderForState(targetDirectory);
  const defaultName = normalizedTarget === normalizeFolderForState(currentParent)
    ? duplicateName(currentName, directory)
    : currentName;
  const name = window.prompt("复制后的名称", defaultName);
  if (!name) return;
  try {
    await api("/api/copy", {
      method: "POST",
      body: { projectId: state.projectId, path, directory, targetDirectory, name }
    });
    await loadFiles();
    if (state.auditOpen) await loadAudit();
  } catch (error) {
    state.error = errorMessage(error);
    render();
  }
}

async function handleContextAction(action: string) {
  const item = state.contextMenu;
  if (!item) return;
  state.contextMenu = null;
  render();
  if (action === "download") {
    triggerBrowserDownload(item.path, item.name);
    return;
  }
  if (action === "fastDownload") {
    await downloadFast(item.path, item.name, Number(item.size || 0));
    return;
  }
  if (action === "rename") {
    await renamePath(item.path, item.directory, item.name);
    return;
  }
  if (action === "move") {
    await movePath(item.path, item.directory);
    return;
  }
  if (action === "copy") {
    await copyPath(item.path, item.directory, item.name);
    return;
  }
  if (action === "takeOwnership") {
    if (!window.confirm(`接管 ${item.name}？原所属用户将不能再删除它。`)) return;
    await takeOwnership(item.path, item.directory);
    return;
  }
  if (action === "delete") {
    const message = item.directory ? `删除 ${item.name} 及其全部文件？` : `删除 ${item.name}？`;
    if (!window.confirm(message)) return;
    await deletePath(item.path, item.directory);
  }
}

function triggerBrowserDownload(path: string, name: string) {
  const params = new URLSearchParams({ projectId: state.projectId, path });
  const link = document.createElement("a");
  link.href = `/api/direct/download?${params}`;
  link.download = name;
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

async function downloadFast(path: string, name: string, size: number) {
  const item: TransferItem = {
    id: crypto.randomUUID(),
    kind: "download",
    name,
    loaded: 0,
    total: size,
    status: "downloading",
    speedBps: 0,
    lastLoaded: 0,
    lastSpeedAt: Date.now()
  };
  state.uploads.push(item);
  render();

  let target: { kind: "file"; writable: FileSystemWritable } | { kind: "memory"; chunks: Blob[] } | null = null;
  try {
    const source = await getDownloadSource(path);
    const downloadSize = source.size || size;
    item.total = downloadSize;

    if (!downloadSize) {
      triggerBrowserDownload(path, name);
      item.status = "done";
      item.note = "已交给浏览器下载";
      renderUploadsOnly();
      return;
    }

    target = await createDownloadTarget(name, downloadSize);
    if (!target) {
      triggerBrowserDownload(path, name);
      item.status = "done";
      item.note = "已交给浏览器下载";
      renderUploadsOnly();
      return;
    }

    const ranges = Array.from({ length: Math.ceil(downloadSize / DOWNLOAD_CHUNK_SIZE) }, (_, index) => {
      const start = index * DOWNLOAD_CHUNK_SIZE;
      const end = Math.min(downloadSize - 1, start + DOWNLOAD_CHUNK_SIZE - 1);
      return { index, start, end };
    });
    let writeChain = Promise.resolve();
    await runConcurrent(ranges, DOWNLOAD_CONCURRENCY, async (range) => {
      const blob = await downloadRangeBlob(source.url, range.start, range.end);
      if (target?.kind === "file") {
        writeChain = writeChain.then(() =>
          target?.kind === "file"
            ? target.writable.write({ type: "write", position: range.start, data: blob })
            : undefined
        );
        await writeChain;
      } else if (target?.kind === "memory") {
        target.chunks[range.index] = blob;
      }
      updateTransferLoaded(item, item.loaded + blob.size);
      renderUploadsOnly();
    });
    await writeChain;

    if (target.kind === "file") {
      await target.writable.close();
    } else {
      triggerBlobDownload(new Blob(target.chunks), name);
    }
    item.status = "done";
    item.note = "完成";
    renderUploadsOnly();
  } catch (error) {
    if (target?.kind === "file") await target.writable.abort?.().catch(() => undefined);
    item.status = "failed";
    item.note = "";
    state.error = `${name}: ${errorMessage(error)}`;
    render();
  }
}

async function getDownloadSource(path: string) {
  const params = new URLSearchParams({ projectId: state.projectId, path });
  return await api<DirectDownloadResponse>(`/api/direct/download-url?${params}`);
}

async function createDownloadTarget(name: string, size: number) {
  const picker = (
    window as unknown as {
      showSaveFilePicker?: (options: { suggestedName: string }) => Promise<FileSystemHandle>;
    }
  ).showSaveFilePicker;
  if (picker) {
    const handle = await picker({ suggestedName: name });
    return { kind: "file" as const, writable: await handle.createWritable() };
  }
  if (size > MEMORY_DOWNLOAD_LIMIT) return null;
  return { kind: "memory" as const, chunks: [] as Blob[] };
}

async function downloadRangeBlob(url: string, start: number, end: number) {
  const response = await fetch(url, {
    headers: { range: `bytes=${start}-${end}` }
  });
  if (response.status !== 206) {
    throw new Error(`Range download failed: HTTP ${response.status}`);
  }
  return await response.blob();
}

function triggerBlobDownload(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

async function uploadFiles(files: FolderFile[]) {
  for (const file of files) {
    const relative = file.webkitRelativePath || file.name;
    const remotePath = joinPath(state.prefix, relative);
    const item: TransferItem = {
      id: crypto.randomUUID(),
      kind: "upload",
      name: relative,
      loaded: 0,
      total: file.size,
      status: "queued",
      speedBps: 0,
      lastLoaded: 0,
      lastSpeedAt: Date.now()
    };
    state.uploads.push(item);
    render();
    try {
      await uploadOne(file, remotePath, item);
      item.status = "done";
    } catch (error) {
      item.status = "failed";
      state.error = `${relative}: ${errorMessage(error)}`;
    }
    render();
  }
  await loadFiles();
}

async function uploadOne(file: File, remotePath: string, item: TransferItem) {
  const partSize = DEFAULT_PART_SIZE;
  item.status = "uploading";
  render();
  if (file.size <= partSize) {
    const uploadedDirectly = await uploadSingleDirect(file, remotePath, item);
    if (uploadedDirectly) return;

    const params = new URLSearchParams({
      projectId: state.projectId,
      path: remotePath,
      size: String(file.size)
    });
    await xhrUpload(`/api/upload?${params}`, file, file.type, (loaded) => {
      updateTransferLoaded(item, loaded);
      renderUploadsOnly();
    });
    return;
  }

  const uploadStateId = await multipartStateId(file, state.projectId, remotePath);
  let stored = await getStoredUpload(uploadStateId);
  if (
    !stored ||
    stored.projectId !== state.projectId ||
    stored.path !== remotePath ||
    stored.size !== file.size ||
    stored.lastModified !== file.lastModified
  ) {
    const init = await api<{ uploadId: string; partSize: number }>("/api/upload/init", {
      method: "POST",
      body: {
        projectId: state.projectId,
        path: remotePath,
        contentType: file.type || "application/octet-stream",
        size: file.size
      }
    });
    stored = {
      id: uploadStateId,
      projectId: state.projectId,
      path: remotePath,
      fileName: file.name,
      size: file.size,
      lastModified: file.lastModified,
      contentType: file.type || "application/octet-stream",
      partSize: init.partSize || partSize,
      uploadId: init.uploadId,
      parts: [],
      updatedAt: new Date().toISOString()
    };
    await putStoredUpload(stored);
  } else if (stored.parts.length) {
    item.status = "resuming";
    item.note = `已完成 ${stored.parts.length} 片`;
    renderUploadsOnly();
  }

  const actualPartSize = stored.partSize || partSize;
  const loadedByPart = new Map<number, number>();
  const totalParts = Math.ceil(file.size / actualPartSize);
  for (const part of stored.parts) {
    loadedByPart.set(part.partNumber, multipartPartLength(file.size, actualPartSize, part.partNumber));
  }
  item.loaded = Array.from(loadedByPart.values()).reduce((a, b) => a + b, 0);
  resetTransferSpeed(item);
  renderUploadsOnly();

  try {
    const completed = new Set(stored.parts.map((part) => part.partNumber));
    const pending = Array.from({ length: totalParts }, (_, index) => index + 1).filter(
      (partNumber) => !completed.has(partNumber)
    );
    await runConcurrent(pending, UPLOAD_CONCURRENCY, async (partNumber) => {
      const chunk = file.slice((partNumber - 1) * actualPartSize, partNumber * actualPartSize);
      const response = await uploadPartWithRetry(
        remotePath,
        stored.uploadId,
        chunk,
        file.type || "application/octet-stream",
        item,
        partNumber,
        (loaded) => {
          loadedByPart.set(partNumber, loaded);
          updateTransferLoaded(
            item,
            Array.from(loadedByPart.values()).reduce((a, b) => a + b, 0)
          );
          renderUploadsOnly();
        }
      );
      loadedByPart.set(partNumber, chunk.size);
      stored.parts = upsertPart(stored.parts, response);
      stored.updatedAt = new Date().toISOString();
      await putStoredUpload(stored);
    });

    await api("/api/upload/complete", {
      method: "POST",
      body: {
        projectId: state.projectId,
        path: remotePath,
        uploadId: stored.uploadId,
        size: file.size,
        parts: stored.parts
      }
    });
    await deleteStoredUpload(stored.id);
  } catch (error) {
    item.note = "重新选择同一文件可继续";
    throw error;
  }
}

async function uploadSingleDirect(file: File, remotePath: string, item: TransferItem) {
  if (!state.user?.directR2) return false;

  try {
    const contentType = file.type || "application/octet-stream";
    const direct = await api<DirectUploadResponse>("/api/direct/upload", {
      method: "POST",
      body: {
        projectId: state.projectId,
        path: remotePath,
        contentType,
        size: file.size
      }
    });
    await xhrUploadRequest(direct.url, file, direct.headers || {}, (loaded) => {
      updateTransferLoaded(item, loaded);
      renderUploadsOnly();
    }, false);
    await api("/api/direct/upload/complete", {
      method: "POST",
      body: {
        projectId: state.projectId,
        path: remotePath,
        r2Key: direct.r2Key,
        size: file.size
      }
    });
    return true;
  } catch {
    item.note = "直连失败，改用代理";
    item.loaded = 0;
    resetTransferSpeed(item);
    renderUploadsOnly();
    return false;
  }
}

async function uploadPartWithRetry(
  remotePath: string,
  uploadId: string,
  chunk: Blob,
  contentType: string,
  item: TransferItem,
  partNumber: number,
  onProgress: (loaded: number) => void
) {
  let lastError: unknown;
  for (let attempt = 1; attempt <= 3; attempt += 1) {
    try {
      item.status = attempt === 1 ? "uploading" : "retrying";
      item.note = attempt === 1 ? "" : `第 ${partNumber} 片重试 ${attempt - 1}/2`;
      renderUploadsOnly();
      return await uploadMultipartPart(remotePath, uploadId, partNumber, chunk, contentType, onProgress);
    } catch (error) {
      lastError = error;
      if (attempt < 3) await delay(600 * attempt);
    }
  }
  throw lastError;
}

async function uploadMultipartPart(
  remotePath: string,
  uploadId: string,
  partNumber: number,
  chunk: Blob,
  contentType: string,
  onProgress: (loaded: number) => void
) {
  if (state.user?.directR2) {
    try {
      const direct = await api<DirectUploadResponse>("/api/direct/upload/part", {
        method: "POST",
        body: {
          projectId: state.projectId,
          path: remotePath,
          uploadId,
          partNumber
        }
      });
      const response = await xhrUploadRequest(direct.url, chunk, direct.headers || {}, onProgress, false);
      const etag = normalizeMultipartEtag(response.headers.etag);
      if (!etag) throw new Error("Missing ETag from direct R2 upload");
      return { partNumber, etag };
    } catch {
      onProgress(0);
    }
  }

  const params = new URLSearchParams({
    projectId: state.projectId,
    path: remotePath,
    uploadId,
    partNumber: String(partNumber)
  });
  return await xhrUploadJson<UploadedPart>(`/api/upload/part?${params}`, chunk, contentType, onProgress);
}

function multipartPartLength(size: number, partSize: number, partNumber: number) {
  const start = (partNumber - 1) * partSize;
  return Math.max(0, Math.min(partSize, size - start));
}

function upsertPart(parts: UploadedPart[], next: UploadedPart) {
  const map = new Map(parts.map((part) => [part.partNumber, part]));
  map.set(next.partNumber, next);
  return Array.from(map.values()).sort((a, b) => a.partNumber - b.partNumber);
}

function normalizeMultipartEtag(etag?: string) {
  return String(etag || "")
    .trim()
    .replace(/^W\//, "")
    .replace(/^"|"$/g, "");
}

async function multipartStateId(file: File, projectId: string, remotePath: string) {
  const material = [projectId, remotePath, file.name, file.size, file.lastModified].join("\n");
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(material));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

let transferDbPromise: Promise<IDBDatabase> | null = null;

function transferDb() {
  if (!transferDbPromise) {
    transferDbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open("shinano-workspace-transfers", 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore("uploads", { keyPath: "id" });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error("IndexedDB open failed"));
    });
  }
  return transferDbPromise;
}

async function getStoredUpload(id: string): Promise<StoredMultipartUpload | null> {
  const db = await transferDb();
  return new Promise((resolve, reject) => {
    const request = db.transaction("uploads", "readonly").objectStore("uploads").get(id);
    request.onsuccess = () => resolve((request.result as StoredMultipartUpload | undefined) || null);
    request.onerror = () => reject(request.error || new Error("IndexedDB read failed"));
  });
}

async function putStoredUpload(upload: StoredMultipartUpload) {
  const db = await transferDb();
  await new Promise<void>((resolve, reject) => {
    const request = db.transaction("uploads", "readwrite").objectStore("uploads").put(upload);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error || new Error("IndexedDB write failed"));
  });
}

async function deleteStoredUpload(id: string) {
  const db = await transferDb();
  await new Promise<void>((resolve, reject) => {
    const request = db.transaction("uploads", "readwrite").objectStore("uploads").delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error || new Error("IndexedDB delete failed"));
  });
}

async function runConcurrent<T>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<void>
) {
  let next = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      await worker(items[index], index);
    }
  });
  await Promise.all(runners);
}

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function installBeforeUnloadGuard() {
  window.addEventListener("beforeunload", (event) => {
    if (!hasActiveTransfers()) return;
    event.preventDefault();
    event.returnValue = "";
  });
}

function installContextMenuDismiss() {
  window.addEventListener("click", (event) => {
    if (!state.contextMenu) return;
    const target = event.target as Element | null;
    if (target?.closest(".context-menu")) return;
    state.contextMenu = null;
    render();
  });
  window.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !state.contextMenu) return;
    state.contextMenu = null;
    render();
  });
  window.addEventListener("scroll", () => {
    if (!state.contextMenu) return;
    state.contextMenu = null;
    render();
  }, true);
}

function hasActiveTransfers() {
  return state.uploads.some((item) => ACTIVE_TRANSFER_STATUSES.has(item.status));
}

function confirmDiscardActiveTransfers() {
  if (!hasActiveTransfers()) return true;
  return window.confirm("还有上传或下载任务正在进行。确定要离开吗？");
}

function updateTransferLoaded(item: TransferItem, loaded: number) {
  const now = Date.now();
  const elapsed = Math.max(now - item.lastSpeedAt, 1);
  const delta = Math.max(0, loaded - item.lastLoaded);
  item.loaded = loaded;
  if (elapsed >= 300 || item.status === "done") {
    const instantSpeed = (delta * 1000) / elapsed;
    item.speedBps = item.speedBps ? item.speedBps * 0.35 + instantSpeed * 0.65 : instantSpeed;
    item.lastLoaded = loaded;
    item.lastSpeedAt = now;
  }
}

function resetTransferSpeed(item: TransferItem) {
  item.speedBps = 0;
  item.lastLoaded = item.loaded;
  item.lastSpeedAt = Date.now();
}

function renderUploadsOnly() {
  const panel = document.querySelector(".upload-panel");
  if (panel) {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = renderUploads();
    panel.replaceWith(wrapper.firstElementChild || document.createElement("div"));
  }
}

async function api<T>(
  path: string,
  options: { method?: string; body?: unknown } = {}
): Promise<T> {
  const method = options.method || "GET";
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers["content-type"] = "application/json";
  if (method !== "GET" && method !== "HEAD" && state.user?.csrfToken) {
    headers["x-csrf-token"] = state.user.csrfToken;
  }
  const response = await fetch(path, {
    method,
    headers: Object.keys(headers).length ? headers : undefined,
    body: options.body === undefined ? undefined : JSON.stringify(options.body)
  });
  const text = await response.text();
  const data = text ? JSON.parse(text) : null;
  if (!response.ok) {
    throw new Error(data?.error || `HTTP ${response.status}`);
  }
  return data as T;
}

function xhrUpload(
  url: string,
  body: Blob,
  contentType: string,
  onProgress: (loaded: number) => void
) {
  return xhrUploadRequest(
    url,
    body,
    { "content-type": contentType || "application/octet-stream" },
    onProgress,
    true
  ).then(() => undefined);
}

async function xhrUploadJson<T>(
  url: string,
  body: Blob,
  contentType: string,
  onProgress: (loaded: number) => void
) {
  const response = await xhrUploadRequest(
    url,
    body,
    { "content-type": contentType || "application/octet-stream" },
    onProgress,
    true
  );
  return JSON.parse(response.text) as T;
}

function xhrUploadRequest(
  url: string,
  body: Blob,
  headers: Record<string, string>,
  onProgress: (loaded: number) => void,
  includeCsrf: boolean
) {
  return new Promise<XhrUploadResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [name, value] of Object.entries(headers)) {
      xhr.setRequestHeader(name, value);
    }
    if (includeCsrf && state.user?.csrfToken) xhr.setRequestHeader("x-csrf-token", state.user.csrfToken);
    xhr.upload.onprogress = (event) => onProgress(event.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ text: xhr.responseText, headers: parseXhrHeaders(xhr.getAllResponseHeaders()) });
      }
      else reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(body);
  });
}

function parseXhrHeaders(raw: string) {
  const headers: Record<string, string> = {};
  for (const line of raw.trim().split(/[\r\n]+/)) {
    const index = line.indexOf(":");
    if (index <= 0) continue;
    headers[line.slice(0, index).trim().toLowerCase()] = line.slice(index + 1).trim();
  }
  return headers;
}

function currentProject() {
  return state.user?.projects.find((project) => project.id === state.projectId) || null;
}

function hasPermission(permission: string) {
  return Boolean(currentProject()?.permissions.includes(permission));
}

function canDeleteItem(item: { owner?: string; createdBy?: string }) {
  if (hasPermission("deleteAny")) return true;
  const owner = item.owner || item.createdBy;
  return Boolean(hasPermission("deleteOwn") && owner && owner === state.user?.username);
}

function canTakeOwnership(item: { owner?: string; createdBy?: string }) {
  const owner = item.owner || item.createdBy;
  return Boolean(hasPermission("takeOwnership") && owner && owner !== state.user?.username);
}

function canRenameItem(item: { owner?: string; createdBy?: string }) {
  if (!hasPermission("rename")) return false;
  if (hasPermission("deleteAny") || hasPermission("takeOwnership")) return true;
  const owner = item.owner || item.createdBy;
  return Boolean(owner && owner === state.user?.username);
}

function canMoveItem(item: { owner?: string; createdBy?: string }) {
  return canRenameItem(item);
}

function canCopyItem() {
  return hasPermission("copy");
}

function renderActor(actor?: string, timestamp?: string) {
  if (!actor && !timestamp) return "未记录";
  const who = actor ? escapeHtml(actor) : "未知";
  const when = timestamp ? formatDate(timestamp) : "未知时间";
  return `${who}<br><span class="meta-time">${when}</span>`;
}

function joinPath(base: string, name: string) {
  return [base, name]
    .join("/")
    .replace(/\\/g, "/")
    .split("/")
    .filter(Boolean)
    .join("/");
}

function normalizeFolderForState(path: string) {
  const clean = joinPath("", path);
  return clean ? `${clean}/` : "";
}

function parentFolder(path: string) {
  const clean = path.replace(/\/$/, "");
  const index = clean.lastIndexOf("/");
  return index >= 0 ? clean.slice(0, index) : "";
}

function duplicateName(name: string, directory: boolean) {
  const clean = name.replace(/\/$/, "");
  if (directory) return `${clean} 副本`;
  const index = clean.lastIndexOf(".");
  if (index <= 0) return `${clean} 副本`;
  return `${clean.slice(0, index)} 副本${clean.slice(index)}`;
}

function basename(path: string) {
  return path.replace(/\/$/, "").split("/").pop() || path;
}

function transferStatusLabel(item: TransferItem) {
  const status = {
    queued: "排队中",
    uploading: "上传中",
    resuming: "续传中",
    downloading: "下载中",
    retrying: "重试中",
    done: "完成",
    failed: "失败"
  }[item.status];
  return {
    upload: `上传 ${status}`,
    download: `下载 ${status}`
  }[item.kind];
}

function actionLabel(action: string) {
  return (
    {
      "folder.create": "新建文件夹",
      "folder.delete": "删除文件夹",
      "file.create": "上传文件",
      "file.upload.recover": "恢复上传",
      "file.update": "更新文件",
      "file.delete": "删除文件",
      "file.download": "下载文件",
      "file.ownership.take": "接管文件",
      "folder.ownership.take": "接管文件夹",
      "file.rename": "重命名文件",
      "folder.rename": "重命名文件夹",
      "file.move": "移动文件",
      "folder.move": "移动文件夹",
      "file.copy": "复制文件",
      "folder.copy": "复制文件夹",
      "filesystem.reindex": "重建文件索引"
    }[action] || action
  );
}

function auditPathLabel(event: AuditEvent) {
  if (event.path && event.targetPath) return `${event.path} -> ${event.targetPath}`;
  return event.path || "-";
}

function formatBytes(size: number) {
  if (size < 1024) return `${size} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = size / 1024;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return `${value.toFixed(value >= 10 ? 1 : 2)} ${units[index]}`;
}

function formatSpeed(bytesPerSecond: number) {
  const safeSpeed = Math.max(0, bytesPerSecond);
  const megabits = (safeSpeed * 8) / 1_000_000;
  const decimals = megabits >= 10 ? 1 : 2;
  return `${formatBytes(safeSpeed)}/s (${megabits.toFixed(decimals)} Mbps)`;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeAttr(value: string) {
  return escapeHtml(value).replace(/'/g, "&#39;");
}
